/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { Fragment, ReactNode } from 'react';

import {
  Grid,
  Heading,
  SkeletonBlock,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';

import { isolateComponent } from '@zapier/style-encapsulation';

import { useSize } from '@zapier/upsells/src/hooks';
import { Size } from '@zapier/upsells/src/types';

const styles = {
  buttonGroup: ({
    forceButtonsToSeparateLines,
    size,
  }: {
    forceButtonsToSeparateLines: boolean | undefined;
    size: Size;
  }) =>
    css({
      display: 'grid',
      gap: 10,
      gridTemplateColumns:
        size === 'small' || forceButtonsToSeparateLines
          ? 'repeat(fit-content, 100%)'
          : 'auto auto',
    }),

  contentWrapper: (size: Size) =>
    css({
      padding: size === 'small' ? 0 : '30px 20px 30px 30px',
    }),

  heading: {
    ...Typography.sectionHeader3,
    marginBottom: 10,
  },

  imageContainer: (imageBackgroundColor: string) => ({
    alignItems: 'center',
    background: imageBackgroundColor,
    display: 'flex',
    justifyContent: 'center',
    paddingLeft: 30,
  }),

  subtitle: {
    ...Typography.smallPrint1,
  },
};

const BodyText = ({ children }: { children: ReactNode }) => (
  <Text color="gray60" tag="p" type="paragraph3">
    {children}
  </Text>
);

type Props = {
  bodyText: ReactNode | null;
  buttons: ReactNode[] | null;
  /**
   * Useful when button text is too long for both buttons to fit inline
   * in the nudge without truncating text.
   */
  forceButtonsToSeparateLines?: boolean;
  headingText: ReactNode | null;
  /**
   * Used for identifying views in tests.
   */
  id: string;
  image: ReactNode | null;
  imageBackgroundColor?: string;
  subtitleText: string | null | undefined;
};

export const UpgradeNudgeLayout = ({
  bodyText,
  buttons,
  forceButtonsToSeparateLines,
  headingText,
  id,
  image,
  imageBackgroundColor = 'none',
  subtitleText,
}: Props) => {
  const size = useSize();

  return (
    <div data-testid={id}>
      <Grid
        columns={size === 'small' ? 'auto' : '470px minmax(100px, 300px)'}
        gap={0}
      >
        <div css={styles.contentWrapper(size)}>
          <Heading css={styles.subtitle}>{subtitleText}</Heading>

          <Spacer height={10} />

          {headingText ? (
            <Heading css={styles.heading}>{headingText}</Heading>
          ) : (
            <Fragment>
              <SkeletonBlock height={50} width="100%" />
              <Spacer height={10} />
            </Fragment>
          )}

          {bodyText ? (
            <BodyText>{bodyText}</BodyText>
          ) : (
            <SkeletonBlock height={65} width="100%" />
          )}

          <Spacer height={20} />

          {buttons ? (
            <div
              css={styles.buttonGroup({ forceButtonsToSeparateLines, size })}
            >
              {buttons}
            </div>
          ) : (
            <SkeletonBlock height={50} width="100%" />
          )}
        </div>

        <div css={styles.imageContainer(imageBackgroundColor)}>
          {size !== 'small' && (image ? image : <SkeletonBlock width="100%" />)}
        </div>
      </Grid>
    </div>
  );
};
